/**
 * Voice Reference Manage View
 *
 * Subview used to attach or manage a character's voice reference clip. The
 * actual upload/preview/remove UI lives in the shared `VoiceReferenceUploader`
 * component, which is also used inside the Character Studio create form.
 */

import React from 'react';
import { CharacterRig, CharacterVoiceReference } from '../../types/RiggingTypes';
import {
  withVoiceReference,
  withoutVoiceReference,
  getCharacterVoiceAsset,
} from '../../services/riggedCharacterService';
import { MAX_VOICE_REFERENCE_SECONDS } from '../../services/characterStorageService';
import { Mic } from '@mui/icons-material';
import VoiceReferenceUploader from './VoiceReferenceUploader';

interface VoiceReferenceProps {
  character: CharacterRig;
  // eslint-disable-next-line no-unused-vars
  onSave: (rig: CharacterRig) => void | Promise<void>;
  onCancel: () => void;
}

const VoiceReference: React.FC<VoiceReferenceProps> = ({
  character,
  onSave,
  onCancel,
}) => {
  const voiceAsset = getCharacterVoiceAsset(character);

  const handleUploaded = (voiceReference: CharacterVoiceReference) => {
    void onSave(withVoiceReference(character, voiceReference));
  };

  const handleRemove = () => {
    void onSave(withoutVoiceReference(character));
  };

  return (
    <div className="voice-reference-container">
      <div className="voice-reference-header">
        <Mic style={{ fontSize: 20, color: '#7be7f0' }} />
        <div>
          <h3>Voice reference</h3>
          <p>
            A short {MAX_VOICE_REFERENCE_SECONDS}-second audio clip that tells
            us how “{character.name || character.imageName}” sounds.
          </p>
        </div>
      </div>

      <VoiceReferenceUploader
        voiceUrl={character.voiceUrl ?? null}
        fileName={voiceAsset?.name}
        durationSeconds={
          typeof voiceAsset?.metadata?.durationSeconds === 'number'
            ? voiceAsset.metadata.durationSeconds
            : undefined
        }
        onUploaded={handleUploaded}
        onRemove={handleRemove}
        characterName={character.name || character.imageName}
      />

      <div className="action-buttons">
        <button className="action-btn cancel" onClick={onCancel}>
          Back to studio
        </button>
      </div>
    </div>
  );
};

export default VoiceReference;